// src/pages/PrivacyPolicyPage.js
//
// Discloses the analytics and advertising tags by name, purpose, legal basis,
// retention, and how to withdraw consent, plus a per-category cookie table.
//
// NOTE: drafted for accuracy against how the site actually behaves, but the
// legal-basis and retention wording for a health-data site with EU/UK traffic
// should be reviewed by a qualified privacy professional before you rely on it.

import React from 'react';
import { Container, Table } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { openPreferences } from '../utils/consent';

const cellTop = { verticalAlign: 'top' };

function PrivacyPolicyPage() {
  return (
    <>
      <Helmet>
        <title>Privacy &amp; Cookie Policy | Benign Fasciculation Syndrome</title>
        <meta
          name="description"
          content="How benignfasciculationsyndrome.org uses cookies and tracking, including Google Analytics and the Meta Pixel, and how to manage or withdraw your consent."
        />
      </Helmet>

      <div style={{ backgroundColor: '#003f5c', color: '#fff', padding: '4rem 0', textAlign: 'center' }}>
        <Container>
          <h1 className="fw-bold">Privacy &amp; Cookie Policy</h1>
          <p style={{ opacity: 0.85 }}>How we handle data and tracking on this site.</p>
        </Container>
      </div>

      <Container className="py-5" style={{ maxWidth: 900 }}>
        <p className="text-muted">Last updated: July 2026</p>

        <h2 className="fw-bold mt-4">Our approach</h2>
        <p>
          This site provides information and support for people affected by Benign Fasciculation
          Syndrome. We keep tracking to a minimum and, outside of what is strictly necessary to run
          the site, nothing loads until you choose. When you first visit, a banner lets you accept or
          reject analytics and advertising cookies. You can change your mind at any time using the{' '}
          <button
            type="button"
            onClick={() => openPreferences()}
            style={{ background: 'none', border: 'none', color: '#003f5c', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}
          >
            Cookie Settings
          </button>{' '}
          link, also in the site footer.
        </p>
        <p>
          Because privacy laws differ by location, the choice we present depends on where you are.
          Visitors in the EU, UK, EEA, and Switzerland see an opt-in banner: analytics and advertising
          stay off until you accept. Visitors elsewhere (for example the US) have these on by default,
          as local law allows, and can turn them off at any time using the same Cookie Settings link.
        </p>

        <h2 className="fw-bold mt-4">Cookie categories</h2>

        <h4 className="mt-3">Strictly necessary — always on</h4>
        <p>
          Required for the site to work: page navigation, the support chat, and remembering your
          cookie choice. These do not track you across sites and cannot be switched off. Legal basis:
          our legitimate interest in providing a working website.
        </p>

        <h4 className="mt-3">Analytics — off until you accept</h4>
        <p>
          <strong>Google Analytics 4 (GA4), measurement ID G-6DVXWSZC2L.</strong> Collects aggregated,
          pseudonymous usage data — pages viewed, approximate location, device and browser type,
          and how you move through the site — so we can see what helps people and where to improve.
          IP addresses are anonymised by GA4 and we do not use GA4 for advertising. Legal basis: your
          consent. Provider: Google Ireland Ltd. Typical retention: up to 14 months (Google), and your
          consent record is kept by us for up to 24 months. Google's handling is described in its own
          privacy policy.
        </p>

        <h4 className="mt-3">Advertising — off until you accept</h4>
        <p>
          <strong>Meta Pixel, ID 4367299196862647.</strong> Used to measure the effectiveness of our
          outreach on Meta platforms. Because our content is health-related, Meta applies additional
          restrictions to this dataset. The Pixel is not even loaded unless you accept this category.
          Legal basis: your consent. Provider: Meta Platforms Ireland Ltd. Handling is described in
          Meta's own data policy.
        </p>

        <h2 className="fw-bold mt-4">Cookies we may set</h2>
        <div style={{ overflowX: 'auto' }}>
          <Table bordered responsive size="sm" className="mt-2">
            <thead>
              <tr>
                <th>Cookie</th>
                <th>Provider</th>
                <th>Category</th>
                <th>Purpose</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={cellTop}>cc_cookie</td>
                <td style={cellTop}>This site</td>
                <td style={cellTop}>Necessary</td>
                <td style={cellTop}>Stores your cookie consent choices.</td>
                <td style={cellTop}>6 months</td>
              </tr>
              <tr>
                <td style={cellTop}>_ga</td>
                <td style={cellTop}>Google Analytics</td>
                <td style={cellTop}>Analytics</td>
                <td style={cellTop}>Distinguishes unique visitors.</td>
                <td style={cellTop}>2 years</td>
              </tr>
              <tr>
                <td style={cellTop}>_ga_&lt;id&gt;</td>
                <td style={cellTop}>Google Analytics</td>
                <td style={cellTop}>Analytics</td>
                <td style={cellTop}>Persists session state for GA4.</td>
                <td style={cellTop}>2 years</td>
              </tr>
              <tr>
                <td style={cellTop}>_gid</td>
                <td style={cellTop}>Google Analytics</td>
                <td style={cellTop}>Analytics</td>
                <td style={cellTop}>Distinguishes visitors.</td>
                <td style={cellTop}>24 hours</td>
              </tr>
              <tr>
                <td style={cellTop}>_fbp</td>
                <td style={cellTop}>Meta</td>
                <td style={cellTop}>Advertising</td>
                <td style={cellTop}>Identifies browsers for ad measurement.</td>
                <td style={cellTop}>3 months</td>
              </tr>
              <tr>
                <td style={cellTop}>_fbc</td>
                <td style={cellTop}>Meta</td>
                <td style={cellTop}>Advertising</td>
                <td style={cellTop}>Stores click identifiers for ad measurement.</td>
                <td style={cellTop}>3 months</td>
              </tr>
            </tbody>
          </Table>
        </div>

        <h2 className="fw-bold mt-4">Withdrawing consent</h2>
        <p>
          Open{' '}
          <button
            type="button"
            onClick={() => openPreferences()}
            style={{ background: 'none', border: 'none', color: '#003f5c', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}
          >
            Cookie Settings
          </button>{' '}
          and reject a category. We immediately stop sending data to that provider and clear the
          related cookies from your browser. Withdrawing consent does not affect processing that
          already took place while consent was active.
        </p>

        <h2 className="fw-bold mt-4">Contact</h2>
        <p>
          Questions about this policy or your data? Reach us at{' '}
          <a href="mailto:info@benignfasciculationsyndrome.org">info@benignfasciculationsyndrome.org</a>.
        </p>

        <p className="text-muted mt-4" style={{ fontSize: '0.85rem' }}>
          This policy explains our cookie and tracking practices. It is not legal advice.
        </p>
      </Container>
    </>
  );
}

export default PrivacyPolicyPage;
